import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Breadcrumbs,
    Button,
    Card,
    CardActions,
    CardContent,
    Chip,
    CircularProgress,
    Divider,
    Grid,
    IconButton,
    Tab,
    Tabs,
    InputAdornment,
    Link as MuiLink,
    List,
    ListItemButton,
    ListItemText,
    Paper,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import HomeIcon from '@mui/icons-material/Home';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import AddIcon from '@mui/icons-material/Add';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import MenuIcon from '@mui/icons-material/Menu';
import axios from 'axios';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { createEditor } from 'slate';
import { Slate, Editable, withReact } from 'slate-react';
import { resolveSupportAssetUrl } from '../utils/supportAssets';
import SupportTicketWorkspace from './SupportTicketWorkspace';

const EMPTY_CONTENT = [
    {
        type: 'paragraph',
        children: [{ text: '' }],
    },
];

const safeDoc = (val) =>
    Array.isArray(val) && val.length ? val : EMPTY_CONTENT;

const formatDate = (value) => {
    if (!value) return '';
    try {
        const date = new Date(value);
        return new Intl.DateTimeFormat(undefined, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        }).format(date);
    } catch (error) {
        return '';
    }
};

const SupportArticleViewer = ({ content }) => {
    const editor = useMemo(() => withReact(createEditor()), []);
    const doc = useMemo(() => safeDoc(content), [content]);

    // Re-mount Slate when incoming content changes so it re-reads initialValue.
    const [slateKey, setSlateKey] = useState(0);
    useEffect(() => {
        setSlateKey((k) => k + 1);
    }, [doc]);

    const renderElement = useCallback((props) => {
        const { element, attributes, children } = props;
        switch (element.type) {
            case 'heading-one':
                return (
                    <h1 {...attributes} style={{ fontSize: '1.9rem', margin: '24px 0 12px' }}>
                        {children}
                    </h1>
                );
            case 'heading-two':
                return (
                    <h2 {...attributes} style={{ fontSize: '1.5rem', margin: '20px 0 10px' }}>
                        {children}
                    </h2>
                );
            case 'block-quote':
                return (
                    <blockquote
                        {...attributes}
                        style={{
                            borderLeft: '4px solid #90caf9',
                            margin: '20px 0',
                            padding: '8px 20px',
                            color: '#455a64',
                            fontStyle: 'italic',
                            background: '#f1f8ff',
                        }}
                    >
                        {children}
                    </blockquote>
                );
            case 'bulleted-list':
                return (
                    <ul {...attributes} style={{ margin: '12px 0 12px 28px' }}>
                        {children}
                    </ul>
                );
            case 'numbered-list':
                return (
                    <ol {...attributes} style={{ margin: '12px 0 12px 28px' }}>
                        {children}
                    </ol>
                );
            case 'list-item':
                return <li {...attributes}>{children}</li>;
            case 'code':
                return (
                    <pre
                        {...attributes}
                        style={{
                            background: '#263238',
                            color: '#fff',
                            padding: '16px',
                            borderRadius: 8,
                            overflowX: 'auto',
                        }}
                    >
                        <code>{children}</code>
                    </pre>
                );
            case 'image':
                return (
                    <div {...attributes} style={{ textAlign: 'center', margin: '20px 0' }}>
                        <div contentEditable={false}>
                            <img
                                src={resolveSupportAssetUrl(element.url)}
                                alt={element.alt || 'Support visual'}
                                style={{ maxWidth: '100%', borderRadius: 12 }}
                            />
                        </div>
                        {children}
                    </div>
                );
            default:
                return (
                    <p
                        {...attributes}
                        style={{ margin: '12px 0', lineHeight: 1.7, color: '#263238', fontSize: '1rem' }}
                    >
                        {children}
                    </p>
                );
        }
    }, []);

    const renderLeaf = useCallback((props) => {
        const { attributes, children, leaf } = props;
        let style = {};
        if (leaf.bold) {
            style.fontWeight = 'bold';
        }
        if (leaf.italic) {
            style.fontStyle = 'italic';
        }
        if (leaf.underline) {
            style.textDecoration = style.textDecoration ? `${style.textDecoration} underline` : 'underline';
        }
        if (leaf.code) {
            style = {
                ...style,
                background: '#eceff1',
                borderRadius: 4,
                fontFamily: 'monospace',
                padding: '0 4px',
            };
        }
        return (
            <span {...attributes} style={style}>
                {children}
            </span>
        );
    }, []);

    return (
        <Slate key={slateKey} editor={editor} initialValue={doc}>
            <Editable readOnly renderElement={renderElement} renderLeaf={renderLeaf} spellCheck={false} />
        </Slate>
    );
};

const SupportCenter = () => {
    const [categories, setCategories] = useState([]);
    const [categoriesLoading, setCategoriesLoading] = useState(true);
    const [categoriesError, setCategoriesError] = useState('');
    const [expandedCategory, setExpandedCategory] = useState('');

    const [article, setArticle] = useState(null);
    const [articleLoading, setArticleLoading] = useState(false);
    const [articleError, setArticleError] = useState('');

    const [searchTerm, setSearchTerm] = useState('');
    const [refreshing, setRefreshing] = useState(false);

    const navigate = useNavigate();
    const { articleSlug } = useParams();
    const location = useLocation();
    const [activeTab, setActiveTab] = useState('home');
    const [initialTicketId, setInitialTicketId] = useState('');
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    const fetchCategories = useCallback(async () => {
        setCategoriesLoading(true);
        setCategoriesError('');
        try {
            const response = await axios.get('/api/support/categories');
            const list = (response.data || []).map((category) => {
                const articles = (category.articles || []).map((article) => ({
                    ...article,
                    categoryId: category.id,
                    categoryName: category.name,
                }));
                const featuredArticleWithImage = articles.find((article) => Boolean(article.heroImageUrl));
                const categoryImage = category.featuredImageUrl || '';
                return {
                    ...category,
                    articles,
                    featuredImageUrl: categoryImage || featuredArticleWithImage?.heroImageUrl || '',
                };
            });
            setCategories(list);
            setExpandedCategory((prev) => {
                if (prev) return prev;
                const firstCategory = list.find((category) => category.articles && category.articles.length);
                return firstCategory ? firstCategory.id : list[0]?.id || '';
            });
        } catch (error) {
            setCategoriesError('Unable to load support content. Please try again later.');
        } finally {
            setCategoriesLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchCategories();
    }, [fetchCategories]);

    const fetchArticle = useCallback(
        async (slug) => {
            if (!slug) {
                setArticle(null);
                return;
            }
            setArticleLoading(true);
            setArticleError('');
            try {
                const response = await axios.get(`/api/support/articles/${slug}`);
                setArticle(response.data);
            } catch (error) {
                if (error.response && error.response.status === 404) {
                    setArticleError('This article could not be found or is no longer published.');
                } else {
                    setArticleError('Failed to load the article. Please try again later.');
                }
                setArticle(null);
            } finally {
                setArticleLoading(false);
            }
        },
        []
    );

    useEffect(() => {
        fetchArticle(articleSlug);
    }, [articleSlug, fetchArticle]);

    useEffect(() => {
        const searchParams = new URLSearchParams(location.search || '');
        const ticketId = searchParams.get('ticket');
        const tabValue = searchParams.get('tab');
        setInitialTicketId((prev) => {
            const normalized = ticketId || '';
            return prev === normalized ? prev : normalized;
        });
        setActiveTab((prev) => {
            if (ticketId || tabValue === 'tickets') {
                return 'tickets';
            }
            if (articleSlug || tabValue === 'knowledge') {
                return 'knowledge';
            }
            return tabValue || prev;
        });
    }, [location.search, articleSlug]);

    const searchResults = useMemo(() => {
        if (!searchTerm.trim()) {
            return [];
        }
        const term = searchTerm.trim().toLowerCase();
        const results = [];
        categories.forEach((category) => {
            (category.articles || []).forEach((articleSummary) => {
                if (
                    articleSummary.title.toLowerCase().includes(term) ||
                    (articleSummary.summary && articleSummary.summary.toLowerCase().includes(term))
                ) {
                    results.push({
                        ...articleSummary,
                        category,
                    });
                }
            });
        });
        return results;
    }, [categories, searchTerm]);

    const handleArticleSelect = (selectedArticle) => {
        if (!selectedArticle) return;
        setArticleError('');
        setActiveTab('knowledge');
        const categoryId = selectedArticle.categoryId || selectedArticle.category?.id || '';
        if (categoryId) {
            setExpandedCategory(categoryId);
        }
        navigate(`/dashboard/support/${selectedArticle.slug}`);
    };

    const handleRefresh = async () => {
        setRefreshing(true);
        await fetchCategories();
        if (articleSlug) {
            await fetchArticle(articleSlug);
        }
        setRefreshing(false);
    };

    const latestArticles = useMemo(() => {
        const flattened = [];
        categories.forEach((category) => {
            (category.articles || []).forEach((articleSummary) => {
                flattened.push({
                    ...articleSummary,
                    category: {
                        id: category.id,
                        name: category.name,
                    },
                });
            });
        });
        return flattened
            .sort((a, b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0))
            .slice(0, 3);
    }, [categories]);

    const handleTabChange = useCallback(
        (value) => {
            if (value === activeTab && !articleSlug) return;
            setActiveTab(value);
            setMobileMenuOpen(false);
            if (value === 'tickets') {
                navigate('/dashboard/support?tab=tickets', { replace: true });
            } else if (value === 'knowledge') {
                if (articleSlug) {
                    navigate(`/dashboard/support/${articleSlug}`, { replace: true });
                } else {
                    navigate('/dashboard/support?tab=knowledge', { replace: true });
                }
            } else if (value === 'home') {
                navigate('/dashboard/support', { replace: true });
            }
        },
        [activeTab, navigate, articleSlug]
    );

    const handleTicketSelect = useCallback(
        (ticketId) => {
            const searchParams = new URLSearchParams(location.search || '');
            searchParams.set('tab', 'tickets');
            if (ticketId) {
                searchParams.set('ticket', ticketId);
            } else {
                searchParams.delete('ticket');
            }
            const query = searchParams.toString();
            navigate(`/dashboard/support${query ? `?${query}` : ''}`, { replace: true });
            setActiveTab('tickets');
            setInitialTicketId(ticketId || '');
        },
        [location.search, navigate]
    );

    const handleCategoryExplore = (category) => {
        if (!category) return;
        setExpandedCategory(category.id);
        const firstArticle = (category.articles || [])[0];
        if (firstArticle) {
            handleArticleSelect(firstArticle);
        }
    };

    const sidebarContent = (
        <Box sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 4, px: 1 }}>
                <Box
                    sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 2,
                        background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#fff',
                        boxShadow: '0 4px 12px rgba(25, 118, 210, 0.2)',
                    }}
                >
                    <SupportAgentIcon />
                </Box>
                <Typography variant="h6" fontWeight={700} color="text.primary">
                    Support
                </Typography>
            </Box>

            <List sx={{ px: 0 }}>
                {[
                    { id: 'home', label: 'Portal Home', icon: <HomeIcon /> },
                    { id: 'knowledge', label: 'Knowledge Base', icon: <LibraryBooksIcon /> },
                    { id: 'tickets', label: 'My Tickets', icon: <ConfirmationNumberIcon /> },
                ].map((item) => (
                    <ListItemButton
                        key={item.id}
                        selected={activeTab === item.id}
                        onClick={() => handleTabChange(item.id)}
                        sx={{
                            borderRadius: 2.5,
                            mb: 1,
                            py: 1.25,
                            transition: 'all 0.2s',
                            '&.Mui-selected': {
                                backgroundColor: 'rgba(25, 118, 210, 0.08)',
                                color: 'primary.main',
                                '& .MuiListItemIcon-root': { color: 'primary.main' },
                                '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.12)' },
                            },
                            '&:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                            },
                        }}
                    >
                        <Box sx={{ minWidth: 40, color: activeTab === item.id ? 'primary.main' : 'text.secondary' }}>
                            {item.icon}
                        </Box>
                        <ListItemText 
                            primary={item.label} 
                            primaryTypographyProps={{ 
                                fontWeight: activeTab === item.id ? 600 : 500,
                                variant: 'body2' 
                            }} 
                        />
                        {activeTab === item.id && <KeyboardArrowRightIcon fontSize="small" />}
                    </ListItemButton>
                ))}
            </List>

            <Divider sx={{ my: 3, opacity: 0.6 }} />

            <Typography variant="caption" color="text.secondary" sx={{ px: 2, mb: 1, display: 'block', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>
                Resources
            </Typography>
            <List sx={{ px: 0 }}>
                <ListItemButton 
                    sx={{ borderRadius: 2.5, mb: 0.5 }}
                    onClick={() => {
                        setSearchTerm('');
                        navigate('/dashboard/support?tab=tickets&ticket=new');
                    }}
                >
                    <Box sx={{ minWidth: 40, color: 'text.secondary' }}>
                        <AddIcon fontSize="small" />
                    </Box>
                    <ListItemText primary="Submit a Ticket" primaryTypographyProps={{ variant: 'body2' }} />
                </ListItemButton>
            </List>
        </Box>
    );

    const portalHomeView = (
        <Box sx={{ width: '100%' }}>
            <Paper
                elevation={0}
                sx={{
                    p: { xs: 4, md: 8 },
                    borderRadius: 6,
                    background: 'linear-gradient(135deg, #0d47a1 0%, #1976d2 100%)',
                    color: '#fff',
                    mb: 6,
                    position: 'relative',
                    overflow: 'hidden',
                    boxShadow: '0 20px 40px rgba(13, 71, 161, 0.15)',
                }}
            >
                {/* Decorative Elements */}
                <Box sx={{ position: 'absolute', top: -40, right: -40, width: 200, height: 200, borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }} />
                <Box sx={{ position: 'absolute', bottom: 20, right: 60, width: 120, height: 120, borderRadius: '50%', background: 'rgba(255,255,255,0.03)' }} />

                <Box sx={{ position: 'relative', zIndex: 1, textAlign: 'center', maxWidth: 800, mx: 'auto' }}>
                    <Typography variant="h3" fontWeight={800} gutterBottom sx={{ fontSize: { xs: '2rem', md: '3rem' } }}>
                        How can we help?
                    </Typography>
                    <Typography variant="h6" sx={{ mb: 4, opacity: 0.9, fontWeight: 400 }}>
                        Search our knowledge base or get in touch with our experts.
                    </Typography>
                    
                    <Paper
                        elevation={0}
                        sx={{
                            p: 0.5,
                            borderRadius: 4,
                            display: 'flex',
                            alignItems: 'center',
                            backgroundColor: 'rgba(255, 255, 255, 1)',
                            maxWidth: 600,
                            mx: 'auto',
                            boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
                        }}
                    >
                        <InputAdornment position="start" sx={{ pl: 2 }}>
                            <SearchIcon color="action" />
                        </InputAdornment>
                        <TextField
                            fullWidth
                            placeholder="Search for articles, guides, and more..."
                            variant="standard"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && searchTerm.trim()) {
                                    handleTabChange('knowledge');
                                }
                            }}
                            InputProps={{
                                disableUnderline: true,
                                sx: { py: 1.5, fontSize: '1.1rem' }
                            }}
                        />
                        <Button 
                            variant="contained" 
                            size="large"
                            onClick={() => searchTerm.trim() && handleTabChange('knowledge')}
                            sx={{ 
                                borderRadius: 3, 
                                px: 4,
                                textTransform: 'none',
                                fontWeight: 600,
                                boxShadow: 'none'
                            }}
                        >
                            Search
                        </Button>
                    </Paper>
                </Box>
            </Paper>

            <Grid container spacing={4} sx={{ mb: 8 }}>
                <Grid item xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                        <Typography variant="h5" fontWeight={700}>Browse Categories</Typography>
                        <Button 
                            color="primary" 
                            onClick={() => handleTabChange('knowledge')}
                            endIcon={<KeyboardArrowRightIcon />}
                        >
                            View all
                        </Button>
                    </Box>
                    <Grid container spacing={3}>
                        {categories.slice(0, 6).map((category) => (
                            <Grid item xs={12} sm={6} md={4} key={category.id}>
                                <Card 
                                    elevation={0}
                                    onClick={() => handleCategoryExplore(category)}
                                    sx={{ 
                                        height: '100%', 
                                        borderRadius: 4, 
                                        border: '1px solid #f0f0f0',
                                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                        cursor: 'pointer',
                                        '&:hover': {
                                            transform: 'translateY(-8px)',
                                            boxShadow: '0 12px 30px rgba(0,0,0,0.06)',
                                            borderColor: 'primary.light',
                                        }
                                    }}
                                >
                                    <CardContent sx={{ p: 3 }}>
                                        <Box 
                                            sx={{ 
                                                width: 48, 
                                                height: 48, 
                                                borderRadius: 2, 
                                                backgroundColor: 'rgba(25, 118, 210, 0.05)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                color: 'primary.main',
                                                mb: 2
                                            }}
                                        >
                                            <LibraryBooksIcon />
                                        </Box>
                                        <Typography variant="h6" fontWeight={700} gutterBottom>
                                            {category.name}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                            {category.description || `${category.articles?.length || 0} articles available`}
                                        </Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center', color: 'primary.main', fontWeight: 600, fontSize: '0.875rem' }}>
                                            Explore <KeyboardArrowRightIcon fontSize="small" />
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </Grid>
            </Grid>

            <Box sx={{ mb: 8 }}>
                <Typography variant="h5" fontWeight={700} sx={{ mb: 3 }}>Recent Updates</Typography>
                <Grid container spacing={3}>
                    {latestArticles.map((article) => (
                        <Grid item xs={12} md={4} key={article.id}>
                            <Paper
                                elevation={0}
                                onClick={() => handleArticleSelect(article)}
                                sx={{
                                    p: 3,
                                    borderRadius: 4,
                                    border: '1px solid #f0f0f0',
                                    height: '100%',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s',
                                    '&:hover': {
                                        backgroundColor: '#fafafa',
                                        borderColor: 'primary.light',
                                    }
                                }}
                            >
                                <Chip 
                                    label={article.category?.name || 'Article'} 
                                    size="small" 
                                    sx={{ mb: 2, backgroundColor: 'rgba(0,0,0,0.05)', fontWeight: 600 }} 
                                />
                                <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                                    {article.title}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                    {article.summary || 'Click to read more about this topic.'}
                                </Typography>
                                <Typography variant="caption" color="text.disabled">
                                    Last updated {formatDate(article.updatedAt)}
                                </Typography>
                            </Paper>
                        </Grid>
                    ))}
                </Grid>
            </Box>

            <Paper
                elevation={0}
                sx={{
                    p: 4,
                    borderRadius: 5,
                    backgroundColor: '#f8faff',
                    border: '1px solid #eef2f6',
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    alignItems: 'center',
                    gap: 4,
                    textAlign: { xs: 'center', md: 'left' }
                }}
            >
                <Box sx={{ 
                    width: 80, 
                    height: 80, 
                    borderRadius: '50%', 
                    backgroundColor: '#fff', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    boxShadow: '0 8px 20px rgba(0,0,0,0.05)'
                }}>
                    <SupportAgentIcon sx={{ fontSize: 40, color: 'primary.main' }} />
                </Box>
                <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="h6" fontWeight={700}>Still need help?</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Can't find what you're looking for? Open a ticket and our support team will get back to you within 24 hours.
                    </Typography>
                </Box>
                <Button 
                    variant="contained" 
                    size="large" 
                    onClick={() => handleTabChange('tickets')}
                    sx={{ borderRadius: 3, px: 4, fontWeight: 600 }}
                >
                    Contact Support
                </Button>
            </Paper>
        </Box>
    );

    const knowledgeBaseView = (
        <Box sx={{ width: '100%' }}>
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                        xs: '1fr',
                        md: '300px minmax(0, 1fr)',
                    },
                    gap: 4,
                    alignItems: 'start',
                }}
            >
                <Paper
                    elevation={0}
                    sx={{
                        width: '100%',
                        p: 3,
                        borderRadius: 4,
                        border: '1px solid #f0f0f0',
                        alignSelf: 'flex-start',
                        position: { md: 'sticky' },
                        top: { md: 24 },
                        maxHeight: { md: 'calc(100vh - 120px)' },
                        overflowY: { md: 'auto' },
                        backgroundColor: '#fff',
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                        <Typography variant="h6" fontWeight={700}>
                            Knowledge Base
                        </Typography>
                        <Box sx={{ flexGrow: 1 }} />
                        <IconButton size="small" onClick={handleRefresh} disabled={refreshing || categoriesLoading}>
                            {refreshing ? <CircularProgress size={18} /> : <RefreshIcon fontSize="small" />}
                        </IconButton>
                    </Box>

                    <TextField
                        fullWidth
                        placeholder="Filter articles..."
                        value={searchTerm}
                        onChange={(event) => setSearchTerm(event.target.value)}
                        size="small"
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" />
                                </InputAdornment>
                            ),
                            sx: { borderRadius: 2.5, backgroundColor: '#fcfcfc' }
                        }}
                    />

                    <Divider sx={{ my: 3 }} />

                    {categoriesLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <CircularProgress size={24} />
                        </Box>
                    ) : categoriesError ? (
                        <Typography color="error" variant="body2">
                            {categoriesError}
                        </Typography>
                    ) : searchTerm.trim() ? (
                        searchResults.length ? (
                            <List dense>
                                {searchResults.map((result) => (
                                    <ListItemButton
                                        key={`${result.slug}-search`}
                                        selected={result.slug === articleSlug}
                                        onClick={() => handleArticleSelect(result)}
                                        sx={{ borderRadius: 2, mb: 0.5 }}
                                    >
                                        <ListItemText
                                            primary={result.title}
                                            primaryTypographyProps={{ fontWeight: 500 }}
                                            secondary={`${result.category.name}`}
                                        />
                                    </ListItemButton>
                                ))}
                            </List>
                        ) : (
                            <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center', py: 2 }}>
                                No articles found.
                            </Typography>
                        )
                    ) : (
                        <Box>
                            {categories.map((category) => (
                                <Accordion
                                    key={category.id}
                                    disableGutters
                                    expanded={expandedCategory === category.id}
                                    onChange={(event, isExpanded) => {
                                        setExpandedCategory(isExpanded ? category.id : '');
                                    }}
                                    sx={{
                                        '&:before': { display: 'none' },
                                        boxShadow: 'none',
                                        backgroundColor: 'transparent',
                                        border: 'none',
                                        mb: 1,
                                    }}
                                >
                                    <AccordionSummary 
                                        expandIcon={<ExpandMoreIcon />}
                                        sx={{ 
                                            borderRadius: 2.5,
                                            px: 1.5,
                                            '&:hover': { backgroundColor: 'rgba(0,0,0,0.02)' },
                                            '&.Mui-expanded': { minHeight: 48 }
                                        }}
                                    >
                                        <Typography fontWeight={600} variant="body2">{category.name}</Typography>
                                    </AccordionSummary>
                                    <AccordionDetails sx={{ pt: 0, px: 0 }}>
                                        {category.articles.length ? (
                                            <List dense sx={{ pl: 1 }}>
                                                {category.articles.map((articleSummary) => (
                                                    <ListItemButton
                                                        key={articleSummary.id}
                                                        onClick={() =>
                                                            handleArticleSelect({
                                                                ...articleSummary,
                                                                category: {
                                                                    id: category.id,
                                                                    name: category.name,
                                                                },
                                                            })
                                                        }
                                                        selected={articleSummary.slug === articleSlug}
                                                        sx={{ borderRadius: 2, mb: 0.25 }}
                                                    >
                                                        <ListItemText
                                                            primary={articleSummary.title}
                                                            primaryTypographyProps={{ variant: 'body2', color: articleSummary.slug === articleSlug ? 'primary.main' : 'text.primary' }}
                                                        />
                                                    </ListItemButton>
                                                ))}
                                            </List>
                                        ) : (
                                            <Typography variant="caption" color="text.secondary" sx={{ pl: 3 }}>
                                                No articles.
                                            </Typography>
                                        )}
                                    </AccordionDetails>
                                </Accordion>
                            ))}
                        </Box>
                    )}
                </Paper>

                <Box sx={{ minWidth: 0 }}>
                    {articleLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
                            <CircularProgress />
                        </Box>
                    ) : articleError ? (
                        <Paper sx={{ p: 6, textAlign: 'center', borderRadius: 4, border: '1px solid #f0f0f0' }}>
                            <Typography variant="h6" gutterBottom color="error">
                                Error Loading Article
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {articleError}
                            </Typography>
                            <Button variant="outlined" sx={{ mt: 3 }} onClick={() => navigate('/dashboard/support')}>
                                Back to Support
                            </Button>
                        </Paper>
                    ) : article ? (
                        <Paper elevation={0} sx={{ p: { xs: 3, md: 6 }, borderRadius: 4, border: '1px solid #f0f0f0', backgroundColor: '#fff' }}>
                            <Breadcrumbs sx={{ mb: 4, '& .MuiBreadcrumbs-li': { fontSize: '0.875rem' } }}>
                                <MuiLink underline="hover" color="inherit" onClick={() => handleTabChange('home')} sx={{ cursor: 'pointer' }}>
                                    Support
                                </MuiLink>
                                <MuiLink underline="hover" color="inherit" onClick={() => handleTabChange('knowledge')} sx={{ cursor: 'pointer' }}>
                                    Knowledge Base
                                </MuiLink>
                                <Typography color="text.primary">{article.title}</Typography>
                            </Breadcrumbs>

                            <Typography variant="h3" fontWeight={800} sx={{ mb: 2, color: 'text.primary', letterSpacing: '-0.02em' }}>
                                {article.title}
                            </Typography>
                            
                            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mb: 5 }}>
                                <Chip 
                                    label={article.category?.name} 
                                    color="primary" 
                                    size="small" 
                                    sx={{ fontWeight: 600, borderRadius: 1.5 }} 
                                />
                                <Typography variant="body2" color="text.secondary">
                                    Last updated {formatDate(article.updatedAt)}
                                </Typography>
                            </Box>

                            {article.heroImageUrl && (
                                <Box sx={{ mb: 5 }}>
                                    <img
                                        src={resolveSupportAssetUrl(article.heroImageUrl)}
                                        alt={article.title}
                                        style={{ width: '100%', borderRadius: 24, maxHeight: 400, objectFit: 'cover', boxShadow: '0 10px 30px rgba(0,0,0,0.05)' }}
                                    />
                                </Box>
                            )}

                            <Box sx={{ '& p': { mb: 2, fontSize: '1.05rem', lineHeight: 1.8 } }}>
                                <SupportArticleViewer content={article.content} />
                            </Box>
                        </Paper>
                    ) : (
                        <Box sx={{ textAlign: 'center', py: 10 }}>
                            <LibraryBooksIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2, opacity: 0.5 }} />
                            <Typography variant="h5" fontWeight={700} gutterBottom>Select an article</Typography>
                            <Typography variant="body1" color="text.secondary">Choose a category from the sidebar to browse our documentation.</Typography>
                        </Box>
                    )}
                </Box>
            </Box>
        </Box>
    );

    return (
        <Box sx={{ display: 'flex', minHeight: '100vh', backgroundColor: '#fafbfc' }}>
            {/* Sidebar for Desktop */}
            <Box
                sx={{
                    width: 280,
                    flexShrink: 0,
                    display: { xs: 'none', lg: 'block' },
                    borderRight: '1px solid #eef2f6',
                    backgroundColor: '#fff',
                    position: 'sticky',
                    top: 0,
                    height: '100vh',
                    overflowY: 'auto',
                }}
            >
                {sidebarContent}
            </Box>

            {/* Main Content Area */}
            <Box sx={{ flexGrow: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                {/* Mobile Header */}
                <Box
                    sx={{
                        display: { xs: 'flex', lg: 'none' },
                        alignItems: 'center',
                        p: 2,
                        backgroundColor: '#fff',
                        borderBottom: '1px solid #eef2f6',
                        position: 'sticky',
                        top: 0,
                        zIndex: 10,
                    }}
                >
                    <IconButton onClick={() => setMobileMenuOpen(true)}>
                        <MenuIcon />
                    </IconButton>
                    <Typography variant="h6" fontWeight={700} sx={{ ml: 2 }}>
                        Support Portal
                    </Typography>
                </Box>

                <Box sx={{ p: { xs: 2, md: 4, lg: 6 }, pb: 10 }}>
                    {activeTab === 'home' && portalHomeView}
                    {activeTab === 'knowledge' && knowledgeBaseView}
                    {activeTab === 'tickets' && (
                        <SupportTicketWorkspace
                            categories={categories}
                            initialTicketId={initialTicketId}
                            onTicketSelect={handleTicketSelect}
                        />
                    )}
                </Box>
            </Box>

            {/* Mobile Sidebar Drawer (Simple Implementation) */}
            {mobileMenuOpen && (
                <Box
                    sx={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        zIndex: 100,
                        backgroundColor: 'rgba(0,0,0,0.5)',
                        display: { lg: 'none' }
                    }}
                    onClick={() => setMobileMenuOpen(false)}
                >
                    <Box
                        sx={{
                            width: 280,
                            height: '100%',
                            backgroundColor: '#fff',
                            boxShadow: '10px 0 30px rgba(0,0,0,0.1)'
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {sidebarContent}
                    </Box>
                </Box>
            )}
        </Box>
    );

};

export default SupportCenter;
